/**
 * Safe Mixpanel Utilities
 * 
 * Provides safe wrappers for Mixpanel methods that check if Mixpanel
 * is initialized before calling methods. This prevents errors when
 * Mixpanel hasn't finished loading yet.
 */

import mixpanel from 'mixpanel-browser';

/**
 * Check if Mixpanel is initialized and ready to use
 */
export function isMixpanelReady(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.mixpanel !== 'undefined' &&
    window.mixpanel &&
    typeof mixpanel.identify === 'function' &&
    typeof mixpanel.track === 'function'
  );
}

/**
 * Safely identify a user in Mixpanel
 * Returns true if the call was successful, false if Mixpanel wasn't ready
 */
export function safeMixpanelIdentify(distinctId: string): boolean {
  if (!isMixpanelReady()) {
    console.debug('[Mixpanel] Not ready, skipping identify call');
    return false;
  }
  
  try {
    mixpanel.identify(distinctId);
    return true;
  } catch (error) {
    console.warn('[Mixpanel] Error calling identify:', error);
    return false;
  }
}

/**
 * Safely set user properties in Mixpanel
 * Returns true if the call was successful, false if Mixpanel wasn't ready
 */
export function safeMixpanelPeopleSet(properties: Record<string, any>): boolean {
  if (!isMixpanelReady()) {
    console.debug('[Mixpanel] Not ready, skipping people.set call');
    return false;
  }
  
  try {
    if (typeof mixpanel.people?.set === 'function') {
      mixpanel.people.set(properties);
      return true;
    }
    return false;
  } catch (error) {
    console.warn('[Mixpanel] Error calling people.set:', error);
    return false;
  }
}

/**
 * Safely track an event in Mixpanel
 * Returns true if the call was successful, false if Mixpanel wasn't ready
 */
export function safeMixpanelTrack(eventName: string, properties?: Record<string, any>): boolean {
  if (!isMixpanelReady()) {
    console.debug('[Mixpanel] Not ready, skipping track call');
    return false;
  }
  
  try {
    mixpanel.track(eventName, properties);
    return true;
  } catch (error) {
    console.warn('[Mixpanel] Error calling track:', error);
    return false;
  }
}

/**
 * Retry a Mixpanel operation with exponential backoff
 * Useful for operations that must succeed (like identify on sign-in)
 */
export function retryMixpanelOperation<T>(
  operation: () => T,
  maxRetries: number = 10,
  initialDelay: number = 100
): Promise<T | null> {
  return new Promise((resolve) => {
    let retries = 0;
    
    const attempt = () => {
      if (isMixpanelReady()) {
        try {
          const result = operation();
          resolve(result);
          return;
        } catch (error) {
          console.warn('[Mixpanel] Operation failed:', error);
        }
      }
      
      retries++;
      if (retries >= maxRetries) {
        console.warn('[Mixpanel] Max retries reached, giving up');
        resolve(null);
        return;
      }
      
      const delay = initialDelay * Math.pow(2, retries - 1);
      setTimeout(attempt, Math.min(delay, 2000)); // Cap at 2 seconds
    };
    
    attempt();
  });
}

